import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BlogPost {
  id: string;
  user_id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  cover_image_url: string | null;
  status: "draft" | "published";
  category: string | null;
  tags: string[];
  meta_title: string | null;
  meta_description: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
}

export function useBlogPosts(category?: string, search?: string) {
  return useQuery({
    queryKey: ["blog_posts", "published", category, search],
    queryFn: async () => {
      let query = supabase
        .from("blog_posts" as any)
        .select("*")
        .eq("status", "published")
        .lte("published_at", new Date().toISOString())
        .order("published_at", { ascending: false });

      if (category && category !== "all") {
        query = query.eq("category", category);
      }
      if (search) {
        query = query.ilike("title", `%${search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data as unknown as BlogPost[]) ?? [];
    },
  });
}

export function useBlogPost(slug: string) {
  return useQuery({
    queryKey: ["blog_post", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts" as any)
        .select("*")
        .eq("slug", slug)
        .eq("status", "published")
        .single();
      if (error) throw error;
      return data as unknown as BlogPost;
    },
    enabled: !!slug,
  });
}

export function useRelatedPosts(category: string | null, currentId: string) {
  return useQuery({
    queryKey: ["blog_posts", "related", category, currentId],
    queryFn: async () => {
      if (!category) return [];
      const { data, error } = await supabase
        .from("blog_posts" as any)
        .select("*")
        .eq("status", "published")
        .eq("category", category)
        .neq("id", currentId)
        .limit(3);
      if (error) throw error;
      return (data as unknown as BlogPost[]) ?? [];
    },
    enabled: !!category && !!currentId,
  });
}

export function useBlogCategories() {
  return useQuery({
    queryKey: ["blog_categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_categories" as any)
        .select("*")
        .order("name");
      if (error) throw error;
      return (data as unknown as BlogCategory[]) ?? [];
    },
  });
}
